import React from 'react';
import { Card, CardContent } from './ui/card';
import { BarChart3, Briefcase, Star } from 'lucide-react';

const DISC_META = {
  D: { color: '#ef4444', label: { fr: 'Dominance', en: 'Dominance' } },
  I: { color: '#f59e0b', label: { fr: 'Influence', en: 'Influence' } },
  S: { color: '#22c55e', label: { fr: 'Stabilité', en: 'Steadiness' } },
  C: { color: '#3b82f6', label: { fr: 'Conformité', en: 'Compliance' } },
};

const SPHERE_LABELS = {
  FINANCE_AND_LEGAL: { fr: 'Finance et Juridique', en: 'Finance & Legal' },
  PROPERTY: { fr: 'Immobilier', en: 'Property' },
  BUSINESS_SUPPORT: { fr: 'Support Business', en: 'Business Support' },
  LIFESTYLE_SERVICES: { fr: 'Services Lifestyle', en: 'Lifestyle Services' },
  SERVICES_FINANCIERS: { fr: 'Services Financiers', en: 'Financial Services' },
  SERVICES_AUX_ENTREPRISES: { fr: 'Services aux Entreprises', en: 'Business Services' },
  IMMOBILIER_ET_BATIMENT: { fr: 'Immobilier et Bâtiment', en: 'Real Estate & Construction' },
  RH_ET_FORMATION: { fr: 'RH et Formation', en: 'HR & Training' },
  SERVICES_AUX_PERSONNES: { fr: 'Services aux Personnes', en: 'Personal Services' },
  PUBLICITE_ET_COMMUNICATION: { fr: 'Publicité et Communication', en: 'Advertising & Communication' },
  SANTE_BIEN_ETRE: { fr: 'Santé et Bien-être', en: 'Health & Wellness' },
  TELECOM_ET_INFORMATIQUE: { fr: 'Télécom et Informatique', en: 'Telecom & IT' },
};

function getDominant(scores) {
  const entries = [
    ['D', scores.disc_d || 0],
    ['I', scores.disc_i || 0],
    ['S', scores.disc_s || 0],
    ['C', scores.disc_c || 0],
  ].sort((a, b) => b[1] - a[1]);
  const top = [entries[0]];
  if (entries.length > 1 && entries[0][1] - entries[1][1] <= 5) top.push(entries[1]);
  return top;
}

export const Restitution = ({ results, guestName, language = 'en' }) => {
  if (!results) return null;
  const dominant = getDominant(results);
  const values = [results.valeur_1, results.valeur_2, results.valeur_3].filter(Boolean);
  const lang = language === 'fr' ? 'fr' : 'en';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex justify-between items-center bg-gray-900/80 backdrop-blur-sm border-b border-gray-800">
        <img src="/apollo-logo.png" alt="Apollo" className="h-8 w-auto" />
        <span className="text-gray-400 text-sm font-dm-sans">{guestName}</span>
      </header>

      <main className="flex items-center justify-center min-h-screen px-4 pt-24 pb-12">
        <Card className="bg-gray-800/90 border-gray-700 max-w-xl w-full backdrop-blur-sm shadow-xl" data-testid="restitution-card">
          <CardContent className="pt-10 pb-10 px-8">
            {/* Title */}
            <div className="text-center mb-8">
              <p className="text-orange-500 font-manrope font-semibold text-sm uppercase tracking-wider mb-2">
                {lang === 'fr' ? 'Vos résultats' : 'Your results'}
              </p>
              <h1 className="text-2xl sm:text-3xl font-manrope font-bold text-white" data-testid="restitution-title">
                {lang === 'fr' ? 'Profil Comportemental' : 'Behavioral Profile'}
              </h1>
            </div>

            {/* Dominant Badge */}
            <div className="flex items-center justify-center gap-2 mb-8" data-testid="dominant-badge">
              <span className="text-gray-400 text-sm">{lang === 'fr' ? 'Dominance :' : 'Dominant:'}</span>
              {dominant.map(([key, value]) => (
                <span
                  key={key}
                  className="px-4 py-1.5 rounded-full text-white text-sm font-bold"
                  style={{ backgroundColor: DISC_META[key].color }}
                >
                  {DISC_META[key].label[lang]} ({value}%)
                </span>
              ))}
            </div>

            {/* DISC Bars */}
            <div className="mb-8">
              <h3 className="text-white font-manrope font-semibold mb-4 flex items-center gap-2 text-sm">
                <BarChart3 className="w-4 h-4 text-orange-500" />
                {lang === 'fr' ? 'Profil DISC' : 'DISC Profile'}
              </h3>
              <div className="space-y-3">
                {['D', 'I', 'S', 'C'].map((key) => {
                  const value = results[`disc_${key.toLowerCase()}`] || 0;
                  const isDom = dominant.some(([k]) => k === key);
                  return (
                    <div key={key} className="flex items-center gap-3" data-testid={`disc-bar-${key.toLowerCase()}`}>
                      <span
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${isDom ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                        style={{ backgroundColor: DISC_META[key].color }}
                      >
                        {key}
                      </span>
                      <div className="flex-1 h-3 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${value}%`, backgroundColor: DISC_META[key].color }}
                        />
                      </div>
                      <span className={`text-sm w-12 text-right font-semibold ${isDom ? 'text-white' : 'text-gray-400'}`}>
                        {value}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top 3 Values */}
            {values.length > 0 && (
              <div className="mb-8">
                <h3 className="text-white font-manrope font-semibold mb-4 flex items-center gap-2 text-sm">
                  <Star className="w-4 h-4 text-orange-500" />
                  {lang === 'fr' ? 'Top 3 Valeurs' : 'Top 3 Values'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {values.map((v, i) => (
                    <div key={i} className="bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2">
                      <span className="text-orange-500 font-semibold text-sm">#{i + 1} {v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sphere */}
            {results.user_sphere && (
              <div className="pt-6 border-t border-gray-700">
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase className="w-4 h-4 text-orange-500" />
                  <span className="text-gray-400 text-sm">{lang === 'fr' ? "Sphère d'activité" : 'Activity sphere'}</span>
                </div>
                <p className="text-white font-semibold" data-testid="restitution-sphere">
                  {SPHERE_LABELS[results.user_sphere]?.[lang] || results.user_sphere}
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-700 text-center">
              <p className="text-gray-500 text-sm font-dm-sans">
                {lang === 'fr'
                  ? 'Nous nous réjouissons de vous rencontrer lors de notre réunion.'
                  : 'We look forward to meeting you at our meeting.'}
              </p>
              <img src="/apollo-logo.png" alt="Apollo" className="h-6 w-auto mx-auto mt-4 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
